import Container from '@mui/material/Container';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Compound, OfferCompoundService } from '../services/restserver-openapi';
import { useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { CompoundImages } from '../components/campsiteDetailComponents/CompoundImages';
import { CompoundDetailCard } from '../components/campsiteDetailComponents/CompoundDetailCard';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ConstructionIcon from '@mui/icons-material/Construction';
import EmojiPeopleOutlinedIcon from '@mui/icons-material/EmojiPeopleOutlined';
import { CompoundEquipment } from '../components/campsiteDetailComponents/CompoundEquipment';
import { countryCodes as countries } from '../constants/country_code';
import Stack from '@mui/material/Stack';
import PlaceIcon from '@mui/icons-material/Place';
import { EcoFriendlyIcon } from '../components/vanliferComponents/EcoFriendlyIcon';
import Grid from '@mui/material/Grid';
import ExploreIcon from '@mui/icons-material/Explore';
import ChatBubble from '@mui/icons-material/ChatBubble';
import Rating from '@mui/material/Rating';
import {IconButton, PaletteMode, Skeleton, Tooltip} from '@mui/material';
import Button from '@mui/material/Button';
import {  useAppSelector } from '../hooks/hooks';
import HereMapWithMarker from '../components/presentationalComponents/HereMapWithMarker';
import SitesListing from '../components/presentationalComponents/SitesListing';
import Main from '../layouts/Main';
import { alpha, useTheme } from '@mui/material/styles';
import RvHookupIcon from '@mui/icons-material/RvHookup';
import StoriesQuickOverview from "../components/vanliferComponents/Community/StoriesView/StoriesQuickOverview";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {useSnackbar} from "../components/SnackbarProvider";
import { AgentLabel, getLabelsByCompoundId } from '../utils/Agent/labels';
import CompoundInlineLabel from '../components/vanliferComponents/CompoundInlineLabel';

export default function CampsiteDetailPage() {
  const { showSnackbar } = useSnackbar();

  const theme = useTheme();
  const mode = theme.palette.mode;

  const navigate = useNavigate();
  // const generalStyling = createGeneralStyling(theme);
  const isVerifiedAgent = useAppSelector(state => state.user.isVerifiedAgent);

  const { campsiteId } = useParams();

  const vanliferComponentState = useAppSelector(state => state.vanliferComponents);

  const [compound, setCompound] = React.useState<Compound | null>(null);
  const [compoundLabels, setCompoundLabels] = React.useState<AgentLabel[]>([]);

  useEffect(() => {
    if (campsiteId === undefined) {
      return;
    }

    OfferCompoundService.getCompound(parseInt(campsiteId)).then(c => setCompound(c));
  }, [campsiteId]);

  useEffect(() => {
    if (!compound?.compoundId) {
      setCompoundLabels([]);
      return;
    }

    getLabelsByCompoundId(compound.compoundId)
      .then((labels) => setCompoundLabels(labels))
      .catch(() => setCompoundLabels([]));
  }, [compound?.compoundId]);

  function backButton() {
    if (
      !vanliferComponentState.showPaymentComponent &&
      !vanliferComponentState.showBookedComponent) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }

  const AverageRating = () => {
    if (compound === null) {
      return <Skeleton variant="rectangular" width={185} height={20} />;
    }

    if (!compound.overallRating) {
      return null;
    }

    return (
        <Box display="flex" alignItems="center" flexWrap="wrap">
          <Box display="flex" flexDirection="column" gap={0}>
            <Box display="flex" alignItems="center" gap={1}>
              <Rating
                  value={compound.overallRating}
                  precision={0.1}
                  readOnly
                  size="medium"
              />
              <Typography>({compound.overallRating.toFixed(1)})</Typography>
              <Tooltip title="Die Bewertung ergibt sich aus den Stories. Bei mehreren Stories pro Aufenthalt wird der Durchschnitt ihrer Bewertungen genommen.">
                <IconButton size="small">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
    );
  };

  const EcoFriendly = () => {
    if (compound?.sustainable) {
      return (
        <Typography sx={{ color: '#13ab1a' }}>
          {/* Text here */}
          <EcoFriendlyIcon sx={{ ml: 1, position: 'relative', top: '6px' }} />
          Eco
        </Typography>
      );
    }

    return null;
  };

  const isLoading = compound === null;
  let details: React.ReactNode;
  let title: React.ReactNode;

  if (isLoading) {
    title = <Skeleton variant="rounded" width={500} height={50} />;
    details = (
      <>
        <Skeleton variant="rounded" width="100%" height={160} />
        <Skeleton variant="rounded" width="100%" height={120} />
        <Skeleton variant="rounded" width="100%" height={95} />
      </>
    );
  } else {
    title = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Box component="span">{compound.name}</Box>
        {compoundLabels.map((label) => (
          <CompoundInlineLabel key={label.labelId} label={label} />
        ))}
      </Box>
    );
    details = (
      <>
        <CompoundDetailCard icon={<PlaceIcon />} title="Adresse">
          <Typography>{compound.addressLine1}</Typography>
          <Typography>{compound.zipCode} {compound.city}</Typography>
          <Typography>{countries.find((country) => country.countryCode === compound.country)?.countryName}</Typography>
        </CompoundDetailCard>

        <CompoundDetailCard icon={<ExploreIcon />} title="Standort">
          <Typography variant={'body1'}
                      color={'text.secondary'}>{compound.coordinates?.x}, {compound.coordinates?.y}</Typography>
          {compound.coordinates?.x && compound.coordinates?.y &&
            <HereMapWithMarker mapMarker={{ latitude: compound.coordinates.x, longitude: compound.coordinates.y }}
                               width="100%" height="300px" />}
        </CompoundDetailCard>


      </>
    );
  }

  return (
    <Main>
      <Box bgcolor={theme.palette.alternate} marginBottom={2}>
        <Container>
          <Button variant={'contained'} color={'primary'} onClick={backButton}
                  sx={{ marginTop: '2rem', marginBottom: '2rem' }}>Zurück</Button>
        </Container>
      </Box>

      <Box bgcolor={theme.palette.alternate} marginBottom={2}>
        <Container>
          <Grid container spacing={4}>
            <Grid item container xs={12} md={12} alignItems={'center'}>
              <Box>
                <Stack direction="row" sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <AverageRating />
                </Stack>

                <Stack direction="row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 1, gap: 2 }}>
                <Box >
                  <Typography
                    variant="h3"
                    color="text.primary"
                    sx={{ fontWeight: 700 }}
                  >
                    <Typography
                      color={'text.primary'}
                      component={'span'}
                      variant={'inherit'}
                      sx={{
                        background: `linear-gradient(180deg, transparent 82%, ${alpha(
                          mode === 'light' as PaletteMode ? theme.palette.secondary.main : theme.palette.primary.main,
                          0.3,
                        )} 0%)`,
                      }}
                    >
                      {title}
                    </Typography>
                  </Typography>
                </Box>
                <Stack
                  spacing={1}
                  sx={{ ml: { xs: 0, md: 3 }, minHeight: { md: 56 }, justifyContent: 'center' }}
                  alignItems={{ xs: 'flex-start', md: 'flex-end' }}
                >
                  <EcoFriendly />
                </Stack>
              </Stack>

                <Box marginBottom={3}>
                  <Typography variant="h6" component="p" color="text.secondary">
                    Alle Infos mit Bildern und Stories
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'stretched', sm: 'flex-start' }}
                >
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/*<Divider sx={{marginTop:0, marginBottom:0}}/>*/}

      <Box bgcolor={theme.palette.alternate} marginBottom={2}>
        <Container>




          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack spacing={2}>
                {details}
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack spacing={2}>
                <CompoundDetailCard icon={<PhotoLibraryIcon />} title="Bilder">
                  <CompoundImages compound={compound} />
                </CompoundDetailCard>
              </Stack>
            </Grid>
          </Grid>

        </Container>
      </Box>

      <Box bgcolor={theme.palette.alternate} marginBottom={2}>
        <Container>
          <Grid container spacing={4}>
            <Grid item container xs={12} alignItems={'center'}>
              <CompoundDetailCard icon={<EmojiPeopleOutlinedIcon />} title="Ihr Gastgeber stellt sich vor...">
                <Typography>{compound?.guestInformation}</Typography>
              </CompoundDetailCard>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box bgcolor={theme.palette.alternate} marginBottom={2}>
        <Container>
          <Grid container spacing={4}>
            <Grid item container xs={12} alignItems={'center'}>
              <CompoundDetailCard icon={<ConstructionIcon />} title="Ausstattung">
                <CompoundEquipment compound={compound} layout="text" largeIcons={false} showTextIfNoEquipment={true} />
              </CompoundDetailCard>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box bgcolor={theme.palette.alternate} marginBottom={2}>
        <Container>
          <CompoundDetailCard icon={<ChatBubble />} title="Stories">
            {compound?.compoundId && <StoriesQuickOverview compoundId={compound.compoundId} showSnackbar={showSnackbar} />}
          </CompoundDetailCard>
        </Container>
      </Box>

      {isVerifiedAgent &&
          <Box bgcolor={theme.palette.alternate} marginBottom={2}>
            <Container>
            <CompoundDetailCard icon={<RvHookupIcon />} title="Stellplätze">
              <Box>
                {compound?.compoundId && <SitesListing compoundId={compound.compoundId} />}
              </Box>
            </CompoundDetailCard>
            </Container>
          </Box>
      }
      <Box bgcolor={theme.palette.alternate} marginBottom={2}>
        <Container>
          <Button variant={'contained'} color={'primary'} onClick={backButton}
                  sx={{ marginBottom: '2rem' }}>Zurück</Button>
        </Container>
      </Box>
    </Main>
  );
}
